import { Controller } from "@hotwired/stimulus"

// Quick-add-inventory modal for the PO-import review screen. Lets an operator
// add stock for an out-of-stock / low-stock matched product IN PLACE instead
// of opening the WMS receiving page in a new tab (which loses the in-progress
// review). One modal per page; open(event) fills it from the clicked
// "Add inventory →" button's dataset, submit() POSTs to the (tenant-scoped)
// quick_receive endpoint via fetch and flips that line's stock badge to green
// on success.
//
// Targets:
//   overlay      - the full-screen dialog backdrop
//   productName  - read-only product name display
//   quantity     - qty <input>
//   location     - location <select>
//   error        - inline error area inside the modal
//   submitButton - the "Add to inventory" button (disabled while in flight)
//   toast        - the success confirmation toast
export default class extends Controller {
    static targets = ["overlay", "productName", "quantity", "location", "error", "submitButton", "toast"]
    static values = { url: String }

    open(event) {
        const btn = event.currentTarget
        this.productId = btn.dataset.productId
        this.badgeId   = btn.dataset.badgeId

        this.productNameTarget.textContent = btn.dataset.productName || "Selected product"
        const needed = parseInt(btn.dataset.neededQty, 10)
        this.quantityTarget.value = Number.isFinite(needed) && needed > 0 ? needed : 1

        this.hideError()
        this.overlayTarget.style.display = "flex"
        this.quantityTarget.focus()
        this.quantityTarget.select()
    }

    close() {
        this.overlayTarget.style.display = "none"
    }

    backdropClick(event) {
        // Only close when the click is on the backdrop itself, not the dialog.
        if (event.target === this.overlayTarget) this.close()
    }

    keydown(event) {
        if (event.key === "Escape") this.close()
    }

    async submit() {
        const quantity   = parseInt(this.quantityTarget.value, 10)
        const locationId = this.locationTarget.value

        if (!Number.isFinite(quantity) || quantity <= 0) {
            return this.showError("Enter a positive quantity.")
        }
        if (!locationId) {
            return this.showError("Pick a location.")
        }

        this.hideError()
        this.submitButtonTarget.disabled = true

        const token = document.querySelector('meta[name="csrf-token"]')?.content

        try {
            const response = await fetch(this.urlValue, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-Token": token || ""
                },
                body: JSON.stringify({
                    product_id:  this.productId,
                    location_id: locationId,
                    quantity:    quantity
                })
            })

            const data = await response.json().catch(() => ({}))

            if (!response.ok || !data.ok) {
                return this.showError(data.error || "Couldn't add inventory. Please try again.")
            }

            this.markInStock(data.available)
            this.decrementHeadsUp()
            const locationCode = this.locationTarget.selectedOptions[0]?.textContent?.trim() || "location"
            this.showToast(`Added ${data.received} × ${data.product_name} into ${locationCode}.`)
            this.close()
        } catch (err) {
            this.showError("Network error — couldn't add inventory.")
        } finally {
            this.submitButtonTarget.disabled = false
        }
    }

    markInStock(available) {
        if (!this.badgeId) return
        const badge = document.getElementById(this.badgeId)
        if (!badge) return
        badge.style.background = "#dcfce7"
        badge.style.color = "#166534"
        badge.textContent = `In stock ✓ (+${available})`
    }

    // Optional heads-up banner: decrement its out-of-stock counter so the
    // page-level summary stays consistent after an in-place add.
    decrementHeadsUp() {
        const counter = document.querySelector("[data-out-of-stock-count]")
        if (!counter) return
        const current = parseInt(counter.dataset.outOfStockCount, 10)
        if (Number.isFinite(current) && current > 0) {
            counter.dataset.outOfStockCount = current - 1
        }
    }

    showToast(message) {
        if (!this.hasToastTarget) return
        this.toastTarget.textContent = message
        this.toastTarget.style.display = "block"
        clearTimeout(this.toastTimeout)
        this.toastTimeout = setTimeout(() => {
            this.toastTarget.style.display = "none"
        }, 4000)
    }

    showError(message) {
        this.errorTarget.textContent = message
        this.errorTarget.style.display = "block"
    }

    hideError() {
        this.errorTarget.textContent = ""
        this.errorTarget.style.display = "none"
    }
}
